import type { CompanyLicensing } from '@/features/plans';
import type { Subscription, SubscriptionItemWithProduct } from '@/features/subscriptions';

export interface SubscriptionEstimate {
  amount: number;
  currency: string;
  interval: string;
  scope: 'LICENSES' | 'LEGACY_CATALOG';
}

/** Contract values are not evidence that an external subscription has been updated. */
export function subscriptionEstimate(
  licensing: CompanyLicensing | undefined,
  subscription: Subscription | null,
  items: SubscriptionItemWithProduct[],
): SubscriptionEstimate | null {
  if (!licensing) return null;
  if (licensing.mode === 'ENFORCED') {
    const current = licensing.current;
    if (!current || current.plan.deletedAt || current.assignment.endedAt) return null;
    const { unitAmount, seatCount, currency, interval } = current.assignment;
    const additionalChannels = current.assignment.additionalConnectionCount ?? 0;
    if (!Number.isSafeInteger(unitAmount) || unitAmount <= 0 || !Number.isSafeInteger(seatCount) || seatCount < 1
      || !Number.isSafeInteger(additionalChannels) || additionalChannels < 0
      || !Number.isSafeInteger(unitAmount * (seatCount + additionalChannels)) || !currency || !interval) return null;
    // Add-ons on the financial subscription may belong to a different plan/currency.
    return { amount: unitAmount * (seatCount + additionalChannels), currency, interval, scope: 'LICENSES' };
  }
  const plan = subscription?.plan;
  if (!plan || !Number.isSafeInteger(plan.price) || plan.price < 0 || !plan.currency || !plan.interval) return null;
  let amount = plan.price;
  for (const item of items) {
    if (item.status && !['ACTIVE', 'SCHEDULED_REMOVAL'].includes(item.status)) continue;
    if (item.product.billingType && item.product.billingType !== 'RECURRING') continue;
    if (!Number.isSafeInteger(item.quantity) || item.quantity < 0 || !Number.isSafeInteger(item.priceAtMoment) || item.priceAtMoment < 0) return null;
    amount += item.priceAtMoment * item.quantity;
    if (!Number.isSafeInteger(amount)) return null;
  }
  return { amount, currency: plan.currency, interval: plan.interval, scope: 'LEGACY_CATALOG' };
}
